import { faCheck, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { displayPopin } from "../../Hook/popins";
import useUser from "../../Hook/user";
import { FilterDefinition, FilterOption } from "./catalog";
import FilterSheet from "./FilterSheet";

/** A checkbox row: 20px box on the left, 14.5px label, optional 11.5px explanation. */
function OptionRow({
  option,
  checked,
  onToggle,
  first,
}: {
  option: FilterOption;
  checked: boolean;
  onToggle: () => void;
  first?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={checked}
      data-testid={`filter-option-${option.value}`}
      className={
        "flex items-center gap-3.5 py-3.5 w-full text-left min-h-tap-min " +
        (first ? "" : "shadow-hairline-top")
      }
    >
      <div
        className={
          "w-checkbox h-checkbox flex-none rounded-md flex items-center justify-center text-[12px] " +
          (checked ? "bg-accent text-ink" : "bg-tint-checkbox text-transparent")
        }
      >
        <FontAwesomeIcon icon={faCheck} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-row">
          {option.dot && <span className={"w-[7px] h-[7px] rounded-full flex-none " + option.dot} />}
          <FormattedMessage id={option.label} />
        </div>
        {option.explain && (
          <div className="text-explain text-muted mt-px">
            <FormattedMessage id={option.explain} />
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * One filter's options. Multi-select, with a clear row at the top that means
 * "do not filter by this". Edits a draft; nothing reaches the feed until Apply.
 */
function FilterOptionSheet({
  definition,
  value,
  onApply,
  onClose,
  onBack,
}: {
  definition: FilterDefinition;
  value: any;
  onApply: (value: any) => void;
  onClose: () => void;
  onBack?: () => void;
}) {
  const intl = useIntl();
  const user = useUser();
  const [draft, setDraft] = useState<string[]>(() => {
    if (definition.singleValue) return value === "yes" || value === "no" ? [value] : [];
    return Array.isArray(value) ? [...value] : [];
  });
  const [query, setQuery] = useState("");

  // Sorted against the selection the sheet opened with, not the live draft, so rows
  // do not jump out from under a finger mid-tap.
  const [openedWith] = useState<string[]>(() => draft);

  const options = useMemo(() => {
    const available = definition.options.filter((o) => !o.needsVerifiedAge || user?.informations?.verified_age);
    if (!definition.search) return available;
    return available
      .map((option, index) => ({ option, index }))
      .sort((a, b) => {
        const rank = Number(openedWith.includes(b.option.value)) - Number(openedWith.includes(a.option.value));
        return rank !== 0 ? rank : a.index - b.index;
      })
      .map((entry) => entry.option);
  }, [definition, user, openedWith]);

  const visible = useMemo(() => {
    if (!definition.search || !query.trim()) return options;
    const needle = query.trim().toLowerCase();
    return options.filter((o) => intl.formatMessage({ id: o.label }).toLowerCase().includes(needle));
  }, [options, query, definition.search, intl]);

  const toggle = (option: FilterOption) => {
    // BC9: the free tier stays selectable for guests; the gated tiers answer
    // with the login popin, matching the old per-value rule.
    if (option.guestLocked && user?.informations?.type === "guest") {
      return displayPopin()("login", { errorMessage: "terminated_guest_freetour" });
    }
    // A yes/no filter holds one value, so picking one replaces the other.
    if (definition.singleValue) {
      setDraft(draft.includes(option.value) ? [] : [option.value]);
      return;
    }
    setDraft(
      draft.includes(option.value)
        ? draft.filter((v) => v !== option.value)
        : [...draft, option.value]
    );
  };

  const commit = (next: string[]) => {
    if (definition.singleValue) onApply(next[0] ?? undefined);
    else onApply(next);
  };

  return (
    <FilterSheet
      testId={`filter-sheet-${definition.key}`}
      title={<FormattedMessage id={definition.title} />}
      subtitle={
        draft.length > 0 ? (
          <FormattedMessage id="filter__selected_count" values={{ count: draft.length }} />
        ) : (
          <FormattedMessage id={definition.subtitle} />
        )
      }
      onBack={onBack}
      onClose={onClose}
      onClear={() => commit([])}
      onApply={() => commit(draft)}
    >
      {definition.search && (
        <div className="mt-3.5 flex items-center gap-2.5 px-3.5 py-2.5 rounded-pill bg-tint-neutral">
          <FontAwesomeIcon className="text-muted" icon={faMagnifyingGlass} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={intl.formatMessage({ id: definition.search })}
            className="bg-transparent outline-none text-row w-full placeholder:text-muted"
          />
        </div>
      )}

      <div className="flex flex-col mt-2.5">
        <OptionRow
          first
          option={{ value: "__clear__", label: definition.clearRow }}
          checked={draft.length === 0}
          onToggle={() => setDraft([])}
        />
        {visible.map((option) => (
          <OptionRow
            key={option.value}
            option={option}
            checked={draft.includes(option.value)}
            onToggle={() => toggle(option)}
          />
        ))}
      </div>
    </FilterSheet>
  );
}

export default FilterOptionSheet;
